import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Radio, Users, ShoppingCart, Send, ArrowLeft, Zap, Circle, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatMoney, mediaUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useShareConfig } from '@/context/ShareConfigContext';

// Lazy load Agora only if needed
let AgoraRTC = null;
async function loadAgora() {
    if (!AgoraRTC) {
        const m = await import('agora-rtc-sdk-ng');
        AgoraRTC = m.default || m;
    }
    return AgoraRTC;
}

export default function LivestreamPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const { role } = useAuth();
    const { buildShareUrl } = useShareConfig();
    const [live, setLive] = useState(null);
    const [joinInfo, setJoinInfo] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [wsReady, setWsReady] = useState(false);
    const [buying, setBuying] = useState(false);
    const wsRef = useRef(null);
    const videoRef = useRef(null);
    const clientRef = useRef(null);
    const remoteUsersRef = useRef([]);

    // Load live + join token
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/livestreams/${id}`);
                if (cancelled) return;
                setLive(data);
                if (data.status !== 'live') return;
                const { data: j } = await api.post(`/livestreams/${id}/join`);
                if (cancelled) return;
                setJoinInfo(j);
            } catch (e) { toast.error('No se pudo cargar el live'); }
        })();
        return () => { cancelled = true; };
    }, [id]);

    // Connect Agora if not mock mode
    useEffect(() => {
        if (!joinInfo || joinInfo.mock_mode) return;
        let cleanup = () => {};
        (async () => {
            try {
                const RTC = await loadAgora();
                const client = RTC.createClient({ mode: 'live', codec: 'vp8' });
                await client.setClientRole('audience');
                clientRef.current = client;
                client.on('user-published', async (user, mediaType) => {
                    await client.subscribe(user, mediaType);
                    if (mediaType === 'video' && videoRef.current) {
                        user.videoTrack.play(videoRef.current);
                    }
                    if (mediaType === 'audio') { user.audioTrack.play(); }
                    remoteUsersRef.current.push(user);
                });
                await client.join(joinInfo.app_id, joinInfo.channel_name, joinInfo.token, joinInfo.uid);
                cleanup = async () => {
                    try { await client.leave(); } catch (_) {}
                };
            } catch (e) { console.error(e); toast.error('Error al conectar video'); }
        })();
        return () => { cleanup(); };
    }, [joinInfo]);

    // WebSocket chat
    useEffect(() => {
        if (!live) return;
        const wsUrl = ((process.env.REACT_APP_BACKEND_URL || '') || window.location.origin).replace(/^http/, 'ws') + `/api/ws/live/${live.id}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => setWsReady(true);
        ws.onmessage = (evt) => {
            try {
                const msg = JSON.parse(evt.data);
                if (msg.type === 'message' || msg.type === 'system' || msg.type === 'sale' || msg.type === 'pinned') {
                    setMessages(m => [...m.slice(-199), msg]);
                    if (msg.type === 'pinned') {
                        setLive(l => l ? { ...l, pinned_product_id: msg.product_id, pinned_discount_pct: msg.discount_pct } : l);
                    }
                }
            } catch (_) {}
        };
        ws.onclose = () => setWsReady(false);
        return () => { try { ws.close(); } catch (_) {} };
    }, [live?.id]);

    // heartbeat every 20s to update viewer count
    useEffect(() => {
        if (!live || live.status !== 'live') return;
        const iv = setInterval(() => {
            api.post(`/livestreams/${live.id}/heartbeat`, { livestream_id: live.id, viewer_count: (live.current_viewers || 1), role: 'audience' }).catch(() => {});
        }, 20000);
        return () => clearInterval(iv);
    }, [live?.id, live?.status]);

    const sendMessage = useCallback(async () => {
        if (!text.trim()) return;
        if (role !== 'user') { toast.error('Inicia sesión para chatear'); return; }
        try { await api.post(`/livestreams/${live.id}/message`, { text: text.trim() }); setText(''); } catch (e) { toast.error('Error'); }
    }, [text, live, role]);

    const quickBuy = async (productId) => {
        if (role !== 'user') { nav('/login'); return; }
        setBuying(true);
        try {
            const { data } = await api.post(`/livestreams/${live.id}/quick-checkout`, {
                livestream_id: live.id, product_id: productId,
                origin_url: buildShareUrl('') || window.location.origin,
            });
            if (data.checkout_url) window.location.href = data.checkout_url;
            else toast.success('¡Compra confirmada! (modo demo)');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error de compra'); }
        finally { setBuying(false); }
    };

    if (!live) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Cargando live…</div>;

    const pinned = live.products?.find(p => p.id === live.pinned_product_id) || live.products?.[0];

    return (
        <div className="min-h-screen bg-neutral-950 text-white" data-testid="livestream-page">
            <div className="max-w-7xl mx-auto p-3 md:p-4">
                <Button variant="ghost" size="sm" onClick={() => nav('/')} className="text-white hover:bg-white/10 mb-2"><ArrowLeft className="h-4 w-4 mr-1" />Volver</Button>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    {/* Video */}
                    <div className="lg:col-span-2">
                        <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
                            {joinInfo?.mock_mode !== false ? (
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-red-900 to-black">
                                    <Radio className="h-16 w-16 text-red-500 animate-pulse" />
                                    <p className="text-lg font-semibold">Video en modo demo</p>
                                    <p className="text-xs text-white/70">Configura Agora en Superadmin para video real</p>
                                </div>
                            ) : (
                                <div ref={videoRef} className="w-full h-full" data-testid="live-video" />
                            )}
                            {live.status === 'live' && (
                                <Badge className="absolute top-3 left-3 bg-red-600 text-white gap-1"><Circle className="h-2 w-2 fill-white animate-pulse" />EN VIVO</Badge>
                            )}
                            <Badge className="absolute top-3 right-3 bg-black/60 text-white gap-1"><Users className="h-3 w-3" />{live.current_viewers || 0}</Badge>
                        </div>
                        <div className="mt-3">
                            <h1 className="text-xl md:text-2xl font-semibold" data-testid="live-title">{live.title}</h1>
                            <p className="text-sm text-white/70">@{live.seller_nickname}{live.description ? ` • ${live.description}` : ''}</p>
                        </div>
                        {/* Pinned product */}
                        {pinned && (
                            <Card className="mt-3 bg-white/5 border-white/10" data-testid="live-pinned-product">
                                <CardContent className="p-4 flex items-center gap-3">
                                    {pinned.media?.[0] && <img src={mediaUrl(pinned.media[0].url)} alt="" className="h-16 w-16 rounded-lg object-cover" />}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate">{pinned.title}</div>
                                        <div className="flex items-baseline gap-2">
                                            {live.pinned_discount_pct && pinned.id === live.pinned_product_id ? (
                                                <>
                                                    <span className="text-xl font-bold text-red-500">{formatMoney(pinned.price * (1 - live.pinned_discount_pct/100))}</span>
                                                    <span className="text-xs line-through text-white/60">{formatMoney(pinned.price)}</span>
                                                    <Badge className="bg-red-600 text-white">-{live.pinned_discount_pct}%</Badge>
                                                </>
                                            ) : (
                                                <span className="text-xl font-bold">{formatMoney(pinned.price)}</span>
                                            )}
                                        </div>
                                    </div>
                                    <Button onClick={() => quickBuy(pinned.id)} disabled={buying} className="bg-red-600 hover:bg-red-700 gap-1" data-testid="live-buy-now"><ShoppingCart className="h-4 w-4" />Comprar</Button>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                    {/* Chat */}
                    <div className="flex flex-col h-[70vh] lg:h-auto lg:min-h-[500px] bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2"><MessageCircle className="h-4 w-4" /><span className="font-medium">Chat en vivo</span>{wsReady && <span className="ml-auto text-[10px] text-emerald-400">● conectado</span>}</div>
                        <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="live-chat-messages">
                            {messages.length === 0 && <div className="text-xs text-white/40 text-center py-6">Sé el primero en escribir 👋</div>}
                            {messages.map((m, i) => {
                                if (m.type === 'system') return <div key={i} className="text-xs text-white/60 italic text-center">{m.text}</div>;
                                if (m.type === 'sale') return <div key={i} className="text-xs text-emerald-400 flex items-center gap-1 justify-center bg-emerald-900/20 rounded-full px-3 py-1"><Zap className="h-3 w-3" />@{m.buyer} compró — {formatMoney(m.amount)}</div>;
                                if (m.type === 'pinned') return <div key={i} className="text-xs text-amber-400 text-center">📌 Nuevo producto destacado{m.discount_pct ? ` con ${m.discount_pct}% off` : ''}</div>;
                                return (
                                    <div key={m.id || i} className="flex items-start gap-2">
                                        <span className="text-red-400 font-semibold text-xs">@{m.nickname || 'anon'}:</span>
                                        <span className="text-sm break-words">{m.text}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="p-2 border-t border-white/10 flex gap-1">
                            <Input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} placeholder="Escribe un mensaje…" className="bg-white/10 border-white/20 text-white placeholder-white/40" data-testid="live-chat-input" />
                            <Button size="icon" onClick={sendMessage} className="bg-red-600 hover:bg-red-700" data-testid="live-chat-send"><Send className="h-4 w-4" /></Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
